import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "../lib/api";
import { AppShell, PageHeader } from "../components/AppShell";
import { StatusBadge } from "../components/StatusBadge";
import { PaidBadge, isPaid } from "../components/ReconBadge";
import { money, monthLabel, addMonth, DEMO_MONTH, fmtDateAU } from "../lib/format";
import { ChevronLeft, ChevronRight, Plus, Building2, Receipt, ChevronDown, ChevronUp, RotateCcw, Trash2 } from "lucide-react";
import { TipBanner } from "../components/TipBanner";
import { toast } from "sonner";

const signed = (c) => (c == null ? null : (c > 0 ? "+" : "") + money(c));

export default function MoneyPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [month, setMonth] = useState(DEMO_MONTH);
  const [showLegacy, setShowLegacy] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);
  const [payFilter, setPayFilter] = useState("all");

  const { data: dash } = useQuery({ queryKey: ["dashboard", month], queryFn: async () => (await api.get(`/dashboard?month=${month}`)).data });
  const { data: reports } = useQuery({ queryKey: ["payment-reports", month], queryFn: async () => (await api.get(`/payment-reports?month=${month}`)).data });
  const { data: payments } = useQuery({ queryKey: ["payments", month], queryFn: async () => (await api.get(`/payments?month=${month}`)).data });
  const { data: deleted } = useQuery({ queryKey: ["payment-reports-deleted"], queryFn: async () => (await api.get(`/payment-reports?deleted=true`)).data });

  const visibleReports = (reports || []).filter((r) => payFilter === "all" || (payFilter === "paid" ? isPaid(r) : !isPaid(r)));

  const restore = async (id) => {
    try {
      await api.post(`/payment-reports/${id}/restore`);
      toast.success("Payment report restored");
      qc.invalidateQueries();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <AppShell>
      <PageHeader title="Payments" right={
        <button data-testid="go-expenses" onClick={() => navigate("/money/expenses")}
          className="flex items-center gap-1.5 h-10 px-3.5 rounded-full bg-white border border-slate-200 text-sm font-medium text-[#0D786A] df-pressable">
          <Receipt size={16} /> Expenses
        </button>
      } />

      <TipBanner id="money" text="Tip: Record a payment report as soon as a practice pays you — no need to log individual work days. Molaroo works out what you expected and flags any difference." />

      <div className="px-5 lg:px-8">
        <div className="df-card flex items-center justify-between px-2 py-2 mb-4">
          <button data-testid="money-month-prev" onClick={() => setMonth(addMonth(month, -1))}
            className="h-9 w-9 rounded-full hover:bg-slate-100 flex items-center justify-center df-pressable">
            <ChevronLeft size={20} className="text-slate-600" />
          </button>
          <span className="font-heading font-semibold text-slate-900">{monthLabel(month)}</span>
          <button data-testid="money-month-next" onClick={() => setMonth(addMonth(month, 1))}
            className="h-9 w-9 rounded-full hover:bg-slate-100 flex items-center justify-center df-pressable">
            <ChevronRight size={20} className="text-slate-600" />
          </button>
        </div>
      </div>

      <div className="px-5 lg:px-8 df-fade-in">
        <div className="df-card p-5">
          <div className="space-y-3">
            <div data-testid="money-expected">
              <div className="text-[11px] text-slate-400">Expected</div>
              <div className="font-num font-bold text-2xl text-slate-900 tabular-nums break-words">{money(dash?.expected_cents)}</div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div data-testid="money-received" className="min-w-0">
                <div className="text-[11px] text-slate-400">Received</div>
                <div className="font-num font-bold text-lg text-emerald-700 tabular-nums break-words">{money(dash?.received_cents)}</div>
              </div>
              <div data-testid="money-outstanding" className="min-w-0">
                <div className="text-[11px] text-slate-400">Awaiting</div>
                <div className="font-num font-bold text-lg text-indigo-700 tabular-nums break-words">{money(dash?.outstanding_cents)}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Payment reports — primary workflow */}
      <div className="px-5 lg:px-8 mt-6">
        <h2 className="text-lg font-semibold tracking-tight text-slate-900 mb-3">Payment reports</h2>
        <div className="flex gap-2 mb-3" data-testid="payment-filter">
          {[["all", "All"], ["unpaid", "Unpaid"], ["paid", "Paid"]].map(([v, l]) => (
            <button key={v} data-testid={`filter-${v}`} onClick={() => setPayFilter(v)}
              className={`px-3.5 h-9 rounded-full text-sm font-medium border df-pressable ${payFilter === v ? "bg-[#0D786A] text-white border-[#0D786A]" : "bg-white text-slate-600 border-slate-200"}`}>{l}</button>
          ))}
        </div>
        <div className="space-y-2.5" data-testid="payment-reports-list">
          {visibleReports.length ? visibleReports.map((r) => (
            <button key={r.id} data-testid={`report-card-${r.id}`} onClick={() => navigate(`/money/report/${r.id}`)}
              className="w-full text-left df-card p-4 df-pressable">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="h-8 w-8 rounded-full bg-[#E6F4F1] text-[#0D786A] flex items-center justify-center shrink-0"><Building2 size={15} /></span>
                    <div className="font-medium text-slate-900 truncate">{r.practice_name}</div>
                  </div>
                  <div className="text-xs text-slate-500 mt-1.5">{fmtDateAU(r.period_start)} – {fmtDateAU(r.period_end)}</div>
                </div>
                <PaidBadge report={r} testId={`report-badge-${r.id}`} />
              </div>
              <div className="grid grid-cols-1 min-[380px]:grid-cols-3 gap-x-3 gap-y-1.5 mt-3 pt-3 border-t border-slate-100">
                <div className="flex items-baseline justify-between min-[380px]:block min-w-0"><div className="text-[10px] text-slate-400 shrink-0">Expected</div><div className="font-num font-semibold text-slate-900 text-sm text-right min-[380px]:text-left tabular-nums">{money(r.expected_cents)}</div></div>
                <div className="flex items-baseline justify-between min-[380px]:block min-w-0"><div className="text-[10px] text-slate-400 shrink-0">Actual</div><div className="font-num font-semibold text-sm text-right min-[380px]:text-left tabular-nums text-emerald-700">{r.actual_cents == null ? "—" : money(r.actual_cents)}</div></div>
                <div className="flex items-baseline justify-between min-[380px]:block min-w-0"><div className="text-[10px] text-slate-400 shrink-0">Variance</div><div className={`font-num font-semibold text-sm text-right min-[380px]:text-left tabular-nums ${r.variance_cents > 0 ? "text-emerald-700" : r.variance_cents < 0 ? "text-red-600" : "text-slate-700"}`}>{r.variance_cents == null ? "—" : signed(r.variance_cents)}</div></div>
              </div>
            </button>
          )) : (
            <div className="df-card p-6 text-center" data-testid="reports-empty">
              <div className="font-heading font-semibold text-slate-900">No payment reports yet</div>
              <p className="text-sm text-slate-500 mt-1">Tap “Record Payment” to create your first report — no work days required.</p>
            </div>
          )}
        </div>
      </div>

      {/* By practice */}
      <div className="px-5 lg:px-8 mt-6">
        <h2 className="text-lg font-semibold tracking-tight text-slate-900 mb-3">By practice</h2>
        <div className="space-y-2.5">
          {dash?.per_practice?.length ? dash.per_practice.map((p) => {
            const inner = (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="h-9 w-9 rounded-full bg-[#E6F4F1] text-[#0D786A] flex items-center justify-center shrink-0"><Building2 size={16} /></span>
                  <div className="min-w-0">
                    <div className="font-medium text-slate-900 truncate">{p.name}</div>
                    <div className="text-xs text-slate-500">Received {money(p.received_cents)} of {money(p.expected_cents)}</div>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-num font-semibold text-indigo-700 text-sm">{money(p.outstanding_cents)}</div>
                  <div className="text-[10px] text-slate-400">awaiting</div>
                </div>
              </div>
            );
            return p.has_work ? (
              <button key={p.practice_id} data-testid={`money-practice-${p.practice_id}`}
                onClick={() => navigate(`/practices/${p.practice_id}/check?start=${month}-01&end=${month}-31`)}
                className="w-full text-left df-card p-4 df-pressable">{inner}</button>
            ) : (
              <div key={p.practice_id} data-testid={`money-practice-${p.practice_id}`} className="w-full df-card p-4">{inner}</div>
            );
          }) : <div className="text-sm text-slate-400 px-1">No activity this month.</div>}
        </div>
      </div>

      {/* Recently deleted */}
      {deleted?.length ? (
        <div className="px-5 lg:px-8 mt-6">
          <button data-testid="toggle-deleted" onClick={() => setShowDeleted((s) => !s)}
            className="w-full flex items-center justify-between df-card p-4 df-pressable">
            <span className="flex items-center gap-2 text-sm font-semibold text-slate-700"><Trash2 size={16} className="text-slate-400" /> Recently deleted ({deleted.length})</span>
            {showDeleted ? <ChevronUp size={18} className="text-slate-400" /> : <ChevronDown size={18} className="text-slate-400" />}
          </button>
          {showDeleted && (
            <div className="space-y-2 mt-2" data-testid="deleted-list">
              {deleted.map((r) => (
                <div key={r.id} className="df-card p-4 flex items-center justify-between" data-testid={`deleted-card-${r.id}`}>
                  <div className="min-w-0">
                    <div className="font-medium text-slate-900 truncate">{r.practice_name}</div>
                    <div className="text-xs text-slate-500">{fmtDateAU(r.period_start)} – {fmtDateAU(r.period_end)} · Expected {money(r.expected_cents)}</div>
                  </div>
                  <button data-testid={`restore-${r.id}`} onClick={() => restore(r.id)}
                    className="flex items-center gap-1.5 h-9 px-3 rounded-full bg-[#E6F4F1] text-[#0D786A] text-sm font-medium df-pressable shrink-0">
                    <RotateCcw size={14} /> Restore
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : null}

      {/* Legacy payments (preserved, read-only) */}
      {payments?.length ? (
        <div className="px-5 lg:px-8 mt-6">
          <button data-testid="toggle-legacy" onClick={() => setShowLegacy((s) => !s)}
            className="w-full flex items-center justify-between df-card p-4 df-pressable">
            <span className="flex items-center gap-2 text-sm font-semibold text-slate-700">Earlier payments ({payments.length})</span>
            {showLegacy ? <ChevronUp size={18} className="text-slate-400" /> : <ChevronDown size={18} className="text-slate-400" />}
          </button>
          {showLegacy && (
            <div className="space-y-2 mt-2" data-testid="legacy-list">
              <p className="text-[11px] text-slate-400 px-1">Payments recorded before payment reports. Kept for your records.</p>
              {payments.map((pm) => (
                <div key={pm.id} className="df-card p-4 flex items-center justify-between" data-testid={`payment-row-${pm.id}`}>
                  <div>
                    <div className="font-num font-semibold text-slate-900">{money(pm.amount_cents)}</div>
                    <div className="text-xs text-slate-500">{pm.practice_name} · {fmtDateAU(pm.payment_date)}</div>
                  </div>
                  <StatusBadge status={pm.status} />
                </div>
              ))}
            </div>
          )}
        </div>
      ) : null}

      {/* Bottom clearance so the last card scrolls fully above the floating Record Payment
          action AND the BottomNav. The FAB sits at bottom:6rem+inset and is 3rem tall, so it
          reaches ~9rem+inset up from the viewport bottom; this spacer alone (independent of the
          shell's default padding) exceeds that plus a visual gap, so the final card is never
          obscured on iOS. */}
      <div className="h-[calc(12.5rem+env(safe-area-inset-bottom))] lg:h-24" aria-hidden />

      <button data-testid="record-payment-button" onClick={() => navigate("/money/record")}
        className="fixed bottom-[calc(6rem+env(safe-area-inset-bottom))] lg:bottom-8 left-1/2 -translate-x-1/2 z-40 flex items-center gap-2 px-5 h-12 rounded-full
                   bg-[#0D786A] text-white font-semibold shadow-lg shadow-emerald-900/20 df-pressable">
        <Plus size={20} /> Record Payment
      </button>
    </AppShell>
  );
}
